import { DATE_TIME_FORMAT } from "@lhd/constants";
import {
  useFetchWorkerLogtimesDayChangeTimeV2,
  useUpdateWorkerLogtimesDayChangeTimeV2,
} from "@lhd/hooks";
import { notification, Spin, TimePicker } from "antd";
import type { AxiosError } from "axios";
import moment, { Moment } from "moment";
import { Styles } from "./styled";

interface Props {
  displaySize?: "small" | "medium" | "large";
  isEditMode?: boolean;
}

type DayChangeTimeApiError = {
  message?: string;
  error?: string;
};

const DAY_CHANGE_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_CHANGE_TIME_FORMAT_ERROR =
  "日付変更時刻はHH:MM形式（00:00〜23:59）で入力してください。";

const getErrorMessage = (error: unknown) => {
  const axiosError = error as AxiosError<DayChangeTimeApiError>;
  const status = axiosError.response?.status;
  const serverMessage =
    axiosError.response?.data?.message || axiosError.response?.data?.error;

  if (status === 403) {
    return serverMessage || "日付変更時刻を編集する権限がありません。";
  }
  if (status === 422) {
    return serverMessage || DAY_CHANGE_TIME_FORMAT_ERROR;
  }
  if (status === 400) {
    return serverMessage || "日付変更時刻を入力してください。";
  }

  return serverMessage || "日付変更時刻の保存に失敗しました。";
};

const DayChangeTimeSetting = ({ displaySize, isEditMode }: Props) => {
  const { data, isLoading } = useFetchWorkerLogtimesDayChangeTimeV2();
  const { mutate: updateDayChangeTime, isLoading: isUpdating } =
    useUpdateWorkerLogtimesDayChangeTimeV2();
  const dayChangeTime = data?.day_change_time ?? "";
  const parsedDayChangeTime = moment(
    dayChangeTime,
    DATE_TIME_FORMAT.TIME,
    true,
  );
  const timeValue = parsedDayChangeTime.isValid() ? parsedDayChangeTime : null;

  const handleChange = (time: Moment | null) => {
    const nextValue = time?.format(DATE_TIME_FORMAT.TIME) ?? "";

    if (!DAY_CHANGE_TIME_PATTERN.test(nextValue)) {
      notification.error({ message: DAY_CHANGE_TIME_FORMAT_ERROR });
      return;
    }
    if (nextValue === dayChangeTime) return;

    updateDayChangeTime(
      { day_change_time: nextValue },
      {
        onSuccess: () => {
          notification.success({ message: "保存しました。" });
        },
        onError: (error: unknown) => {
          notification.error({ message: getErrorMessage(error) });
        },
      },
    );
  };
  const getTextLabel = () => {
    return isEditMode ? "翌日出勤への切替時刻" : "翌日出勤への切替時刻:";
  };

  return (
    <Styles text_scale={displaySize}>
      <span>{getTextLabel()}</span>
      <Spin spinning={isLoading || isUpdating}>
        {isEditMode && data?.can_edit_day_change_time === true ? (
          <TimePicker
            value={timeValue}
            format={DATE_TIME_FORMAT.TIME}
            placeholder="HH:MM"
            showNow={false}
            allowClear={false}
            minuteStep={1}
            disabled={isUpdating}
            onChange={handleChange}
          />
        ) : (
          <span className="day-change-time-value">
            {dayChangeTime || "--:--"}
          </span>
        )}
      </Spin>
    </Styles>
  );
};
export default DayChangeTimeSetting;
